import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  Box,
  DialogContent,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Dialog from 'src/components/Dialog/Dialog';
import TableCell from 'src/components/Table/TableCell';
import StatusBadge from 'src/components/StatusBadge';
import ContractStatus from 'src/utils/constants/contracts';
import { formatCurrencyWithDecimals } from 'src/utils/formatAmout';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import useStyles from './terminationKpiStyles';

const STATUS_TONES = {
  [ContractStatus.IN_PROGRESS]: 'success',
  [ContractStatus.ENDED]: 'neutral',
  [ContractStatus.CEDED]: 'info',
  [ContractStatus.CANCELLED]: 'error'
};

const getStatusTone = status => STATUS_TONES[status] || 'neutral';

const CapitalBalanceTerminationKpiDialog = ({
  open,
  handleClose,
  config,
  total,
  contractCount,
  offenderCount,
  offendingContracts
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // offendingContracts is capped by the backend; offenderCount is the real one.
  const listedCount = offendingContracts.length;
  const omittedCount = offenderCount - listedCount;

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      maxWidth={false}
      // The shared Dialog pins its paper to theme.spacing(60) in its own sx,
      // which outranks maxWidth/fullWidth. rest is spread after that sx, so
      // this replaces it — marginBottom included, hence the repeat.
      sx={theme => ({
        marginBottom: '1.5rem',
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: theme.spacing(100)
        }
      })}
      title={
        <>
          <Typography
            className={classes.dialogOverline}
            component="span"
            display="block"
          >
            {t(config.terminationKpiContextKey)}
          </Typography>
          {t('CAPITAL_BALANCE.TERMINATION_KPI_DIALOG_TITLE')}
        </>
      }
    >
      <DialogContent>
        <Typography
          className={classes.description}
          component="p"
          variant="body2"
        >
          {t('CAPITAL_BALANCE.TERMINATION_KPI_DIALOG_DESCRIPTION')}
        </Typography>

        <Box className={classes.statsRow}>
          <Box className={classes.statTile}>
            <Typography className={classes.statLabel} component="span">
              {t('CAPITAL_BALANCE.TERMINATION_KPI_OFFENDERS_LABEL')}
            </Typography>
            <Typography
              className={classes.statValue}
              component="p"
              variant="h2"
            >
              {offenderCount}
            </Typography>
            <Typography
              className={classes.statHelper}
              component="p"
              variant="body2"
            >
              {t('CAPITAL_BALANCE.TERMINATION_KPI_OFFENDERS_HELPER', {
                count: contractCount
              })}
            </Typography>
          </Box>
          <Box className={classes.statTile}>
            <Typography className={classes.statLabel} component="span">
              {t('CAPITAL_BALANCE.TERMINATION_KPI_TOTAL_LABEL')}
            </Typography>
            <Typography
              className={classes.statValue}
              component="p"
              variant="h2"
            >
              {formatCurrencyWithDecimals(total)}
            </Typography>
            <Typography
              className={classes.statHelper}
              component="p"
              variant="body2"
            >
              {t('CAPITAL_BALANCE.TERMINATION_KPI_TOTAL_HELPER')}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.detailSection}>
          <Box className={classes.detailHeader}>
            <Typography className={classes.detailTitle} component="span">
              {t('CAPITAL_BALANCE.TERMINATION_KPI_DETAIL_TITLE')}
            </Typography>
            <Typography
              className={classes.detailCount}
              component="span"
              variant="body2"
            >
              {t('CAPITAL_BALANCE.TERMINATION_KPI_RECORD_COUNT', {
                count: listedCount
              })}
            </Typography>
          </Box>

          {listedCount === 0 ? (
            <Typography
              className={classes.emptyState}
              component="p"
              variant="body2"
            >
              {t('CAPITAL_BALANCE.TERMINATION_KPI_EMPTY')}
            </Typography>
          ) : (
            <Box className={classes.tableWrapper}>
              <Table
                aria-label={t('CAPITAL_BALANCE.TERMINATION_KPI_DETAIL_TITLE')}
                sx={{ '& .MuiTableCell-head': { whiteSpace: 'nowrap' } }}
                stickyHeader
              >
                <TableHead>
                  <TableRow>
                    <TableCell variant="head" align="left">
                      {t('CAPITAL_BALANCE.FOLIO')}
                    </TableCell>
                    <TableCell variant="head" align="left">
                      {t('COMMON.STATUS')}
                    </TableCell>
                    <TableCell variant="head" align="left">
                      {t('CAPITAL_BALANCE.TERMINATION_DATE')}
                    </TableCell>
                    <TableCell variant="head" align="center">
                      {t('CAPITAL_BALANCE.TERM_CONTRACT_VS_DATES')}
                    </TableCell>
                    <TableCell variant="head" align="right">
                      {t('CAPITAL_BALANCE.FINAL_BALANCE')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offendingContracts.map(contract => (
                    <TableRow key={contract.contractId}>
                      <TableCell align="left">{contract.folio}</TableCell>
                      <TableCell align="left">
                        <StatusBadge tone={getStatusTone(contract.status)}>
                          {t(`COMMON.${contract.status}`)}
                        </StatusBadge>
                      </TableCell>
                      <TableCell align="left">
                        {selectFormatDate(
                          contract.terminationDate,
                          dateFormatType.DAY
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        className={clsx(
                          contract.term !== contract.monthsFromDates &&
                            classes.termMismatch
                        )}
                      >
                        {`${contract.term} / ${contract.monthsFromDates}`}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrencyWithDecimals(contract.finalBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {omittedCount > 0 && (
          <Box className={classes.callout}>
            <InfoOutlinedIcon className={classes.calloutIcon} />
            <Typography component="p" variant="body2">
              {t('CAPITAL_BALANCE.TERMINATION_KPI_OMITTED', {
                count: omittedCount
              })}
            </Typography>
          </Box>
        )}

        <Box className={classes.callout}>
          <InfoOutlinedIcon className={classes.calloutIcon} />
          <Typography component="p" variant="body2">
            {t('CAPITAL_BALANCE.TERMINATION_KPI_CALLOUT')}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

CapitalBalanceTerminationKpiDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  contractCount: PropTypes.number.isRequired,
  offenderCount: PropTypes.number.isRequired,
  offendingContracts: PropTypes.array.isRequired
};

export default CapitalBalanceTerminationKpiDialog;
